import { Operation, OperationPayloadCompartmentUpdate, OperationType, Pagination, Trigger } from '@ekkogmbh/apisdk';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { DataTable } from '../../Common/Components/DataTable';
import { CompartmentOperationTriggerDialog } from './CompartmentOperationTriggerDialog';
import { ContentControl } from 'src/Common/Components/ContentControl';
import { Description } from '@mui/icons-material';
import { CompartmentImportPayload } from 'src/OperationGroups/Components/OperationGroupDetailsContent';
import useEkko from 'src/Common/Providers/EkkoProvider';
import { useSearchBar } from 'src/Common/Stores/SearchBarProvider';

export const CompartmentDetailsContent = (): React.JSX.Element => {
  const { api } = useEkko();
  const { setHeaderTitle } = useSearchBar();
  const [triggers, setTriggers] = useState<Trigger[]>([]);
  const { compartmentidentifier } = useParams();
  const navigate = useNavigate();

  const compartmentIdentifier = useMemo(() => compartmentidentifier ?? '', [compartmentidentifier]);

  /** on mount / dismount */
  useEffect(() => {
    setHeaderTitle(compartmentIdentifier);
    return () => {
      setHeaderTitle(undefined);
    };
  }, []);

  const fetchOperations = useCallback(
    (pagination: Pagination) => api.getCompartmentOperations(compartmentIdentifier, pagination),
    [api, compartmentIdentifier],
  );

  const openTriggerDialog = useCallback(
    (operation: Operation) => {
      setTriggers(operation.triggers ?? []);
    },
    [setTriggers],
  );

  const triggerDialog = useMemo(
    () => (
      <CompartmentOperationTriggerDialog
        open={triggers.length > 0}
        onClose={() => setTriggers([])}
        triggers={triggers}
      />
    ),
    [triggers, setTriggers],
  );

  return (
    <ContentControl dialog={triggerDialog}>
      <DataTable
        title={'Operations'}
        onBack={() => navigate(-1)}
        fetchItems={fetchOperations}
        actions={[
          {
            title: 'Details',
            icon: <Description />,
            onClick: (o: Operation) => openTriggerDialog(o),
            disabled: (o: Operation) => o.triggers === undefined || o.triggers.length === 0,
          },
        ]}
        columns={[
          {
            header: 'Coordinate',
            body: (o: Operation) => o.identifier,
            filterField: 'identifier',
            sortField: 'O.identifier',
          },
          {
            header: 'Type',
            body: (o: Operation) => o.type,
            filterField: 'type',
            sortField: 'O.type',
          },
          {
            header: 'Payload',
            body: (o: Operation) => {
              switch (o.type) {
                case OperationType.COMPARTMENT_UPDATE:
                  return <CompartmentImportPayload payload={o.payload as OperationPayloadCompartmentUpdate} />;
                default:
                  return <></>;
              }
            },
          },
        ]}
      />
    </ContentControl>
  );
};
